import { ArrowForward, KeyboardArrowDown, Today } from '@mui/icons-material';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { formatDistance, max, min, subDays, subYears } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  ButtonVariants,
  DatePicker,
  DatePickerVariants,
} from '@/components';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import { Referrer } from '@/types';
import { capitalizeFirstLetter } from '@/utils';

const adjustDate = (date: Date | null) => {
  if (!date) return null;
  return date.toString().includes('T')
    ? new Date(date)
    : new Date(date + 'T12:00:00');
};

export const ALIGNMENTS = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const DateRangeSelector = ({
  fromDate = null,
  toDate = null,
  onDateChange,
  limits,
  alignment = ALIGNMENTS.LEFT,
  referrer = {},
}: {
  fromDate?: Date | null;
  toDate?: Date | null;
  onDateChange: (dateRange: [Date, Date]) => void;
  limits?: {
    min?: Date;
    max?: Date;
  };
  referrer?: Referrer;
  alignment?: (typeof ALIGNMENTS)[keyof typeof ALIGNMENTS];
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(
    fromDate,
  );
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(toDate);
  const [errors, setErrors] = useState<{
    fromDate?: boolean;
    toDate?: boolean;
  }>({});
  const ref = useRef<HTMLDivElement>(null);

  const onCancel = useCallback(() => {
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    setIsOpen(false);
    AnalyticsService.logEvent(EVENTS.DATE_RANGE_EVENTS.CANCELLED, {
      location: referrer,
    });
  }, [fromDate, toDate, referrer]);

  useEffect(() => {
    if (fromDate) {
      setSelectedFromDate(fromDate);
    }
    if (toDate) {
      setSelectedToDate(toDate);
    }
  }, [fromDate, toDate, setSelectedFromDate, setSelectedToDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (document.querySelectorAll('[role="dialog"]')?.length) {
        return;
      }
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        isOpen
      ) {
        onCancel();
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onCancel]);

  // Compose preset date ranges, restricted based on limits
  const presetDateRanges = useMemo(() => {
    const presetDateRanges = [
      {
        label: 'YTD',
        range: [new Date(new Date().getFullYear(), 0, 1), new Date()],
      },
      {
        label: 'Last 7 days',
        range: [subDays(new Date(), 7), new Date()],
      },
      {
        label: 'Last 30 days',
        range: [subDays(new Date(), 30), new Date()],
      },
      {
        label: 'Last year',
        range: [subYears(new Date(), 1), new Date()],
      },
      {
        label: 'All time',
        range: [
          max([new Date('2018-01-02'), ...(limits?.min ? [limits.min] : [])]),
          min([new Date(), ...(limits?.max ? [limits.max] : [])]),
        ],
      },
    ];
    return presetDateRanges.filter(
      preset =>
        (!limits?.min || preset.range[0] >= limits.min) &&
        (!limits?.max || preset.range[1] <= limits.max),
    );
  }, [limits]);

  return (
    <Box
      sx={{
        position: 'relative',
      }}>
      <Box
        sx={[
          {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            whiteSpace: 'nowrap',
            transition: 'background-color 0.3s ease',
            cursor: 'pointer',
            padding: 1.5,
            borderRadius: 1.5,
            minWidth: 290,
            border: 1,
            borderColor: 'grey100',
          },
          isOpen
            ? {
                backgroundColor: 'grey50',
              }
            : {
                '&:hover, &:focus': {
                  backgroundColor: 'grey50',
                },
              },
        ]}
        onClick={e => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
          AnalyticsService.logEvent(
            isOpen
              ? EVENTS.DATE_RANGE_EVENTS.CANCELLED
              : EVENTS.DATE_RANGE_EVENTS.OPENED,
            { location: referrer },
          );
        }}>
        <Today sx={{ width: 20, height: 20 }} />
        {fromDate && toDate ? (
          <Typography
            variant="body2SemiBold"
            sx={{ flexGrow: 1 }}>{`${fromDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })} - ${toDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`}</Typography>
        ) : (
          <Typography variant="body2SemiBold" sx={{ flexGrow: 1 }}>
            Select Date Range
          </Typography>
        )}

        <KeyboardArrowDown
          sx={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </Box>
      <Box
        ref={ref}
        sx={[
          {
            position: 'absolute',
            width: {
              xs: '100%',
              sm: 450,
            },
            p: 2.5,
            mt: 1,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'grey100',
            userSelect: 'none',
            bgcolor: 'white',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            opacity: 0,
            pointerEvents: 'none',
            transform: 'translateY(-10px)',
            zIndex: -1,
            left: alignment === ALIGNMENTS.LEFT ? 0 : 'auto',
            right: alignment === ALIGNMENTS.RIGHT ? 0 : 'auto',
          },
          isOpen && {
            opacity: 1,
            pointerEvents: 'auto',
            transform: 'translateY(0)',
            zIndex: 'overlay',
          },
        ]}
        onClick={() => {
          if (!isOpen) {
            setIsOpen(true);
            AnalyticsService.logEvent(EVENTS.DATE_RANGE_EVENTS.OPENED, {
              location: referrer,
            });
          }
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              alignItems: {
                xs: 'flex-start',
                sm: 'center',
              },
              gap: {
                xs: 1,
                sm: 3,
              },
              width: '100%',
            }}>
            <Box>
              <Typography variant="caption1" sx={{ color: 'text.light' }}>
                START DATE
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <DatePicker
                  fullWidth
                  variant={DatePickerVariants.OUTLINED}
                  allowTextInput={false}
                  allowClear={false}
                  format="MM/dd/yyyy"
                  value={adjustDate(selectedFromDate)}
                  onChange={date => {
                    setSelectedFromDate(date);
                    setErrors({
                      ...errors,
                      fromDate: false,
                    });
                  }}
                  error={errors.fromDate}
                  minDate={limits?.min}
                  maxDate={min([
                    new Date(),
                    ...(limits?.max ? [limits.max] : []),
                    ...(selectedToDate ? [selectedToDate] : []),
                  ])}
                  referrer={{
                    ...referrer,
                    component: EVENT_CONSTANTS.COMPONENT.DATE_RANGE_FROM,
                  }}
                />
              </Box>
            </Box>
            {!isMobile && (
              <ArrowForward
                sx={{ width: 18, height: 18, mt: 3.5, color: 'text.light' }}
              />
            )}
            <Box>
              <Typography variant="caption1" sx={{ color: 'text.light' }}>
                END DATE
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <DatePicker
                  fullWidth
                  variant={DatePickerVariants.OUTLINED}
                  allowTextInput={false}
                  allowClear={false}
                  format="MM/dd/yyyy"
                  value={adjustDate(selectedToDate)}
                  onChange={date => {
                    setSelectedToDate(date);
                    setErrors({
                      ...errors,
                      toDate: false,
                    });
                  }}
                  error={errors.toDate}
                  minDate={max([
                    ...(limits?.min ? [limits.min] : []),
                    ...(selectedFromDate ? [selectedFromDate] : []),
                  ])}
                  maxDate={max([
                    new Date(),
                    ...(limits?.max ? [limits.max] : []),
                  ])}
                  referrer={{
                    ...referrer,
                    component: EVENT_CONSTANTS.COMPONENT.DATE_RANGE_TO,
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
            }}>
            {presetDateRanges.map(preset => (
              <Box
                key={preset.label}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  px: 2,
                  py: 0.5,
                  borderRadius: 6,
                  border: theme => `1.5px solid ${theme.palette.grey100}`,
                  ...(preset.range[0].toLocaleDateString() ===
                    selectedFromDate?.toLocaleDateString() &&
                    preset.range[1].toLocaleDateString() ===
                      selectedToDate?.toLocaleDateString() && {
                      borderColor: 'secondary.border',
                      bgcolor: theme => `${theme.palette.secondary.medium}59`,
                    }),
                }}
                onClick={() => {
                  setSelectedFromDate(preset.range[0]);
                  setSelectedToDate(preset.range[1]);
                  setErrors({
                    fromDate: false,
                    toDate: false,
                  });
                }}>
                <Typography variant="body">{preset.label}</Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '1px',
              bgcolor: 'grey100',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}>
            <Typography variant="body3" sx={{ textAlign: 'center' }}>
              {selectedFromDate && selectedToDate
                ? capitalizeFirstLetter(
                    formatDistance(selectedFromDate, selectedToDate).replace(
                      'about ',
                      '',
                    ),
                  )
                : '-'}
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body3SemiBold">
                {selectedFromDate?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {selectedToDate?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                justifyContent: 'center',
              }}>
              <Button
                variant={ButtonVariants.SECONDARY}
                label="Cancel"
                onClick={onCancel}
              />
              <Button
                variant={ButtonVariants.PRIMARY}
                label="Apply"
                onClick={() => {
                  if (!selectedFromDate || !selectedToDate) {
                    setErrors({
                      fromDate: !selectedFromDate,
                      toDate: !selectedToDate,
                    });
                    return;
                  }
                  onDateChange([selectedFromDate, selectedToDate]);
                  setErrors({
                    fromDate: false,
                    toDate: false,
                  });
                  setIsOpen(false);
                  AnalyticsService.logEvent(EVENTS.DATE_RANGE_EVENTS.APPLIED, {
                    start_date: selectedFromDate.toISOString(),
                    end_date: selectedToDate.toISOString(),
                    preset_label:
                      presetDateRanges.find(
                        preset =>
                          preset.range[0].toLocaleDateString() ===
                            selectedFromDate.toLocaleDateString() &&
                          preset.range[1].toLocaleDateString() ===
                            selectedToDate.toLocaleDateString(),
                      )?.label || 'null',
                    location: referrer,
                  });
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
